import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Heart, ChevronDown, ExternalLink, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'Centenary', href: '/centenary' },
  { name: 'Projects', href: '/projects' },
  { name: 'Competition', href: '/competition' },
  { name: 'News', href: '/news' },
  { name: 'National CPRE', href: 'https://www.cpre.org.uk/', external: true },
];

const moreNavigation = [
  { name: 'Countryside Initiatives', href: '/initiatives' },
  { name: 'Planning News', href: '/planning' },
  { name: 'Opportunities & Volunteering', href: '/volunteering' },
  { name: 'Governance', href: '/governance' },
  { name: 'Resources', href: '/resources' },
  { name: 'Events', href: '/events' },
  { name: 'National Resources', href: 'https://www.cpre.org.uk/resources/', external: true },
];

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-stone-200 shadow-sm" data-testid="main-header">
      <div className="container-custom">
        <div className="flex items-center justify-between py-3">
          {/* Logo - CPRE Official Centenary Logo - Larger */}
          <Link to="/" className="flex-shrink-0" data-testid="header-logo">
            <img 
              src="/cpre-logo-v2-1773858673.png" 
              alt="CPRE Rutland and Lincolnshire - 100 Years" 
              className="h-20 md:h-24 w-auto object-contain hover:opacity-90 transition-opacity duration-200"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-4" data-testid="desktop-nav">
            {navigation.map((item) => (
              item.external ? (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 flex items-center gap-1 text-cpre-new-yew hover:bg-cpre-pebble/50"
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.name}
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    isActive(item.href)
                      ? 'text-cpre-new-yew bg-cpre-pebble'
                      : 'text-foreground/80 hover:text-cpre-new-yew hover:bg-cpre-pebble/50'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.name}
                </Link>
              )
            ))}
            
            {/* More dropdown for remaining items */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 flex items-center gap-1 text-foreground/80 hover:text-cpre-new-yew hover:bg-cpre-pebble/50"
                >
                  More
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {moreNavigation.map((item) => (
                  <DropdownMenuItem key={item.name} asChild>
                    {item.external ? (
                      <a 
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cursor-pointer flex items-center gap-2"
                      >
                        {item.name}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <Link to={item.href} className="cursor-pointer">
                        {item.name}
                      </Link>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 ml-6">
            {/* CTA Button - Donate only */}
            <Link to="/donate" className="hidden sm:block">
              <Button 
                className="bg-cpre-autumn-leaf hover:bg-cpre-autumn-leaf/90 text-white rounded-full px-6 shadow-md shadow-cpre-autumn-leaf/20 font-semibold"
                data-testid="donate-btn"
              >
                <Heart className="w-4 h-4 mr-2" />
                Donate
              </Button>
            </Link>

            {/* Mobile Menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="xl:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-white">
                {/* Mobile Logo */}
                <div className="mb-6 mt-2">
                  <img 
                    src="/cpre-logo-light-bg.png" 
                    alt="CPRE Rutland and Lincolnshire" 
                    className="h-14 w-auto"
                  />
                </div>

                <nav className="flex flex-col gap-1" data-testid="mobile-nav">
                  {navigation.map((item) => (
                    item.external ? (
                      <a
                        key={item.name}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setIsOpen(false)}
                        className="px-4 py-3 text-base font-medium rounded-xl text-cpre-new-yew hover:bg-cpre-pebble/50 flex items-center gap-2"
                      >
                        {item.name}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setIsOpen(false)}
                        className={`px-4 py-3 text-base font-medium rounded-xl transition-colors duration-200 ${
                          isActive(item.href)
                            ? 'text-cpre-new-yew bg-cpre-pebble'
                            : 'text-foreground/80 hover:text-cpre-new-yew hover:bg-cpre-pebble/50'
                        }`}
                      >
                        {item.name}
                      </Link>
                    )
                  ))}
                  
                  {/* More items in mobile */}
                  {moreNavigation.map((item) => (
                    item.external ? (
                      <a
                        key={item.name}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setIsOpen(false)}
                        className="px-4 py-3 text-base font-medium rounded-xl text-cpre-new-yew hover:bg-cpre-pebble/50 flex items-center gap-2"
                      >
                        {item.name}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setIsOpen(false)}
                        className={`px-4 py-3 text-base font-medium rounded-xl transition-colors duration-200 ${
                          isActive(item.href)
                            ? 'text-cpre-new-yew bg-cpre-pebble'
                            : 'text-foreground/80 hover:text-cpre-new-yew hover:bg-cpre-pebble/50'
                        }`}
                      >
                        {item.name}
                      </Link>
                    )
                  ))}

                  <div className="border-t border-stone-200 my-4" />
                  
                  <Link to="/donate" onClick={() => setIsOpen(false)}>
                    <Button className="w-full bg-cpre-autumn-leaf hover:bg-cpre-autumn-leaf/90 text-white rounded-xl font-semibold">
                      <Heart className="w-4 h-4 mr-2" />
                      Donate
                    </Button>
                  </Link>
                  <Link to="/volunteering" onClick={() => setIsOpen(false)}>
                    <Button variant="outline" className="w-full border-2 border-cpre-new-yew text-cpre-new-yew hover:bg-cpre-new-yew hover:text-white rounded-xl mt-2 font-semibold">
                      <Users className="w-4 h-4 mr-2" />
                      Volunteer
                    </Button>
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
